import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import toast from "react-hot-toast";
import { MultiSelect } from "react-multi-select-component";
import { DateRangePicker as DateRangePickerBase } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import { downloadLogs, getLogHistory, deleteAttendanceLog } from "../services/attendanceService";
import { getAllMembers } from "../services/memberService";
import { getActiveBreaks } from "../services/statisticsService";
import { secToHhmm, liveBreakSeconds, liveActualSeconds } from "../utils/liveHours";
import useNow from "../hooks/useNow";
import { dateTimeFormats, scrollToTop } from "../utils/helper";
import Pagination from "../components/common/pagination";
import useConfirm from "../hooks/useConfirm";
import ConfirmModal from "../components/ui/ConfirmModal";
import EditEntryModal from "../components/attendance/EditEntryModal";
import { useBreakDetails, BreakCell, BreakDetailRow } from "../components/attendance/BreakDetails";
import { useReminderDetails, ReminderCell, ReminderDetailRow } from "../components/attendance/ReminderDetails";
import HoursCell from "../components/attendance/HoursCell";
import { SkeletonRows } from "../components/ui/Skeleton";
import "../assets/css/components/date-filter.css";
import "../assets/css/pages/history.css";

const DateRangePicker = DateRangePickerBase;
const fmtTime = (t) => (t ? moment(t, "HH:mm:ss").format("hh:mm a") : null);
// Overnight record: check-out happened on a later calendar date than check-in. Computed on
// the backend (DATE(checkout) > DATE(checkin)) since the frontend only receives time-only values.
const isOvernight = (l) => !!l.isOvernight;
const timeToSec = (t) => {
  if (!t) return 0;
  const [h, m, s] = String(t).split(":").map(Number);
  return (h || 0) * 3600 + (m || 0) * 60 + (s || 0);
};
const secToHm = (sec) => {
  if (!sec) return "—";
  const h = Math.floor(sec / 3600);
  const m = Math.round((sec % 3600) / 60);
  return m ? `${String(h).padStart(2, "0")}h ${String(m).padStart(2, "0")}m` : `${String(h).padStart(2, "0")}h`;
};
// Shape of the server's range-wide KPI aggregate, all zeroed — also the fallback when a
// request fails, so the tiles read 0 rather than showing the previous range's numbers.
const EMPTY_SUMMARY = {
  incompleteSeconds: 0, incompleteDays: 0, totalRows: 0, presentRows: 0,
  lateRows: 0, workedRows: 0, workedSeconds: 0, breakSeconds: 0, breakSessions: 0,
  missedSignOuts: 0,
};

// Per-row status (manual override wins, else derived from the backend isLate flag).
const statusForLog = (l) => {
  const map = { PRESENT: ["Present", "present"], LATE: ["Late", "late"], ON_BREAK: ["On break", "break"], ABSENT: ["Absent", "absent"] };
  if (l.status && map[l.status]) return map[l.status];
  if (l.isAbsent || !l.checkIn) return ["Absent", "absent"];
  return l.isLate ? ["Late", "late"] : ["Present", "present"];
};

const History = () => {
  const [selectedMembers, setSelectedMembers] = useState([]);
  // Open on the last 30 days — a single day rarely says anything useful on landing.
  const [state, setState] = useState([{ startDate: moment().subtract(30, "days").toDate(), endDate: new Date(), key: "selection" }]);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [members, setMembers] = useState([]);
  const [logs, setLogs] = useState([]);
  // Range-wide KPI totals (server aggregates over the whole date + member range, independent
  // of the loaded page and of the type tabs). Every tile except "Records in range" reads these.
  const [summary, setSummary] = useState(EMPTY_SUMMARY);
  const [page, setPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [pageSize] = useState(50);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [histType, setHistType] = useState("All"); // table-head tabs: All | Office | Remote | Late | Break (server-side)
  const [onBreak, setOnBreak] = useState([]); // members on a break right now (for live counters)
  const now = useNow(30000); // ticks the live break / actual-hrs counters on today's rows
  const breakDetails = useBreakDetails(); // expandable break/back breakdown per row
  const reminderDetails = useReminderDetails(); // expandable reminder-type breakdown per row
  const navigate = useNavigate();
  const goToProfile = (id) => id && navigate(`/members/${id}`);
  const { confirm, modalProps } = useConfirm();

  const loadAllMembers = async () => {
    const list = (await getAllMembers()) ?? [];
    // Only active members are filterable here — deactivated/inactive/hidden members are
    // excluded from the history rows and KPIs, so offering them would return nothing.
    // Their own records stay available on their member profile.
    setMembers(
      list
        .filter((m) => m.status === "ACTIVE")
        .map((m) => ({ value: m.memberId, label: m.memberDisplayName || m.memberName, ...m }))
    );
  };

  const loadLogHistory = async () => {
    setLoading(true);
    const r = state[0];
    const { logs = [], pagination, summary: sum } = await getLogHistory({
      startDate: r.startDate,
      endDate: r.endDate,
      memberIds: selectedMembers?.map((m) => m.memberId).join(","),
      page,
      pageSize,
      // Type tabs filter server-side across all pages (only "All" is unfiltered).
      breakOnly: histType === "Break",
      officeOnly: histType === "Office",
      remoteOnly: histType === "Remote",
      lateOnly: histType === "Late",
    });
    scrollToTop();
    setLogs(logs);
    setSummary(sum ?? EMPTY_SUMMARY);
    setTotalItems(pagination?.totalItems || 0);
    setLoading(false);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadAllMembers(); loadLogHistory(); }, []);

  // Poll ONLY the active-breaks list (cheap) so today's rows tick live and stop when a
  // member returns. We deliberately do NOT re-run loadLogHistory on a timer — it calls
  // scrollToTop() and re-fetches the paginated/filtered page, which would disrupt the view.
  useEffect(() => {
    const loadBreaks = async () => setOnBreak((await getActiveBreaks())?.onBreak ?? []);
    loadBreaks();
    const t = setInterval(loadBreaks, 30000);
    return () => clearInterval(t);
  }, []);
  const breakStartByMember = useMemo(() => new Map(onBreak.map((b) => [b.memberId, b.breakStart])), [onBreak]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadLogHistory(); }, [page]);

  // Auto-apply filters: reload whenever the date range or member selection changes.
  // Debounced so the date picker's two onChange events (start + end) and rapid member
  // toggles collapse into a single fetch. Mount-guarded so we don't double-load on first
  // render (the mount effect above already does the initial load).
  const didMount = useRef(false);
  useEffect(() => {
    if (!didMount.current) { didMount.current = true; return; }
    const t = setTimeout(() => {
      if (page !== 1) setPage(1); // the [page] effect reloads with the new filters
      else loadLogHistory();      // already on page 1 → reload directly
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMembers, state]);

  // Type tabs (Office/Remote/Late/Break) filter server-side across all pages, so
  // switching between them must refetch. "All" is unfiltered; All ↔ All stays put.
  const serverFilterKey = histType === "All" ? "" : histType;
  const typeTabMounted = useRef(false);
  useEffect(() => {
    if (!typeTabMounted.current) { typeTabMounted.current = true; return; }
    if (page !== 1) setPage(1); // [page] effect reloads with the new type filter
    else loadLogHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverFilterKey]);

  const handleDownload = async () => {
    setDownloading(true);
    const r = state[0];
    await downloadLogs(
      moment(r.startDate).format(dateTimeFormats.yearMonthDate),
      moment(r.endDate).format(dateTimeFormats.yearMonthDate),
      selectedMembers?.map((m) => m.memberId).join(",")
    );
    setDownloading(false);
  };

  const handleDelete = async (log) => {
    if (!log.id) return;
    const ok = await confirm({ title: "Delete attendance log?", message: "This permanently removes this check-in/out record." });
    if (!ok) return;
    const { status } = await deleteAttendanceLog(log.id);
    if (status === 200) {
      setLogs((prev) => prev.map((l) => (l.id === log.id ? { ...l, id: null, isAbsent: 1, checkIn: null, checkout: null, workingHour: null } : l)));
      toast.success("Log deleted");
    } else toast.error("Could not delete log");
  };

  const dateRangeLabel = () => {
    const r = state[0];
    const s = moment(r.startDate), e = moment(r.endDate);
    if (s.isSame(e, "day")) return s.format("MMM D, YYYY");
    return `${s.format("MMM D")} – ${e.format("MMM D, YYYY")}`;
  };

  const openEdit = (l) => setEditing({ ...l, memberName: l.name, memberDisplayName: l.displayName, memberTitle: l.title, memberImgUrl: l.avatar });
  const applyUpdatedLog = (u) => setLogs((prev) => prev.map((l) => (l.id === u.id ? { ...l, checkIn: u.checkIn, checkout: u.checkout, workingHour: u.workingHour, workType: u.workType, status: u.status, note: u.note } : l)));

  // Straight off the server aggregate, so every tile covers the WHOLE date + member range
  // rather than the 50 rows on screen. Only "Records in range" tracks the type tabs — it is
  // the table's own total; the rest stay range-wide, like Work Shortfall.
  const kpis = useMemo(() => {
    const { totalRows, presentRows, lateRows, workedRows, workedSeconds, breakSeconds, breakSessions, missedSignOuts } = summary;
    const rate = totalRows ? Math.round((presentRows / totalRows) * 100) : 0;
    const avgSec = workedRows ? Math.round(workedSeconds / workedRows) : 0;
    return {
      records: totalItems,
      rate,
      late: lateRows,
      avg: secToHm(avgSec),
      totalBreak: secToHm(breakSeconds),
      breakCount: breakSessions,
      missedSignOuts,
    };
  }, [summary, totalItems]);

  // Type tabs now filter server-side; these branches are harmless no-ops that keep
  // the view correct even if the server returns unfiltered rows.
  const shownLogs = useMemo(() => logs.filter((l) => {
    if (histType === "Office") return l.checkIn && l.workType !== "REMOTE";
    if (histType === "Remote") return l.workType === "REMOTE";
    if (histType === "Late") return l.isLate;
    if (histType === "Break") return l.breakCount > 0;
    return true;
  }), [logs, histType]);

  const firstRow = Math.min(totalItems, (page - 1) * pageSize + 1);
  const lastRow = Math.min(totalItems, pageSize * page);

  return (
    <div onClick={() => setShowDatePicker(false)}>
      {/* KPI strip */}
      <div className="hist-kpis hist-kpis--six">
        {/* <div className="hkpi"><div className="l">Records in range</div><div className="v">{loading ? "…" : kpis.records.toLocaleString()}</div></div> */}
        <div className="hkpi"><div className="l">Avg attendance</div><div className="v" style={{ color: "#34D3A6" }}>{loading ? "…" : `${kpis.rate}%`}</div></div>
        <div className="hkpi"><div className="l">Avg hours / day</div><div className="v" style={{ color: "var(--remote)" }}>{loading ? "…" : kpis.avg}</div></div>
        <div className="hkpi"><div className="l">Late arrivals</div><div className="v" style={{ color: "var(--amber)" }}>{loading ? "…" : kpis.late}</div></div>
        <div className="hkpi"><div className="l">Total break</div><div className="v" style={{ color: "var(--brand)" }}>{loading ? "…" : kpis.totalBreak}</div><div className="s">{loading ? "" : `${kpis.breakCount} break${kpis.breakCount === 1 ? "" : "s"}`}</div></div>
        <div className="hkpi"><div className="l">Work Shortfall</div><div className="v" style={{ color: "var(--pink)" }}>{loading ? "…" : secToHm(summary.incompleteSeconds)}</div><div className="s">{loading ? "" : `${summary.incompleteDays} time${summary.incompleteDays === 1 ? "" : "s"}`}</div></div>
        {/* Purple, not the profile's amber: Late arrivals already owns amber on this strip. */}
        <div className="hkpi" title="Signed in but never signed out — needs the sign-out added by hand"><div className="l">Missed sign-outs</div><div className="v" style={{ color: "var(--purple)" }}>{loading ? "…" : kpis.missedSignOuts}</div>
          {/* <div className="s">{loading ? "" : "awaiting a manual sign-out"}</div> */}
        </div>
      </div>

      {/* Table — filter controls now live INSIDE the table header (hist-head),
          matching Startise Attendance.dc.html (no separate filter card).
          overflow:visible lets the date popover / member dropdown escape the card. */}
      <div className="card" style={{ marginTop: 16, overflow: "visible" }}>
        <div className="hist-head">
          {/* left: date-range + member filter + Filter action + type chips */}
          <div className="filter-wrap" style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <div style={{ position: "relative" }} onClick={(e) => e.stopPropagation()}>
              <button className="btn filter-trigger" onClick={() => setShowDatePicker(!showDatePicker)}>
                <svg><use href="#i-cal" /></svg>{dateRangeLabel()}
              </button>
              {showDatePicker && (
                <div className="date-popover" style={{ position: "absolute", top: 46, left: 0, zIndex: 99, boxShadow: "var(--shadow)", borderRadius: 12, overflow: "hidden", border: "1px solid var(--border)" }}>
                  <DateRangePicker onChange={(item) => setState([item.selection])} moveRangeOnFirstSelection={false} months={1} ranges={state} direction="vertical" />
                </div>
              )}
            </div>
            <div className="filter-member" style={{ minWidth: 240 }} onClick={(e) => e.stopPropagation()}>
              <MultiSelect
                options={members}
                value={selectedMembers}
                onChange={setSelectedMembers}
                labelledBy="Select members"
                hasSelectAll={false}
                overrideStrings={{ selectSomeItems: "Filter by members…" }}
              />
            </div>
            {/* <div className="hist-tabs"> */}
              {["All", "Office", "Remote", "Late", "Break"].map((t) => (
                <button key={t} className={`hist-tab ${histType === t ? "on" : ""}`} onClick={() => setHistType(t)}>{t}</button>
              ))}
            {/* </div> */}
          </div>
          <button className="btn btn-ghost" onClick={handleDownload} disabled={downloading}>
            <svg><use href="#i-download" /></svg>{downloading ? "Exporting…" : "Export range"}
          </button>
        </div>

        {/* Header + rows scroll together; the filter bar above and pagination below stay put. */}
        <div className="table-scroll">
        <div className="hist-grid hist-colhead">
          <div>Date</div><div>Member</div><div>In</div><div>Out</div><div>Hours</div><div>Break</div><div title="Reminders" aria-label="Reminders"><svg className="colhead-ic"><use href="#i-bell" /></svg></div><div>Type</div><div>Status</div><div></div>
        </div>

        {loading && <SkeletonRows count={8} />}
        {!loading && shownLogs.length === 0 && <div className="hist-empty">No logs for this range.</div>}

        {!loading && shownLogs.map((l, idx) => {
          const remote = l.workType === "REMOTE";
          const [sLabel, sCls] = statusForLog(l);
          // Only today's in-progress row ticks live; all historical rows use stored values.
          const liveBreakSec = liveBreakSeconds(l, breakStartByMember.get(l.memberId), now);
          const liveActualSec = liveActualSeconds(l, liveBreakSec, now);
          const breakStr = liveActualSec != null ? secToHhmm(liveBreakSec) : secToHm(timeToSec(l.totalBreakDuration));
          const hoursStr = liveActualSec != null ? secToHhmm(liveActualSec) : secToHm(timeToSec(l.actualWorkingHour || l.workingHour));
          const brkOpen = breakDetails.isOpen(l.id);
          // Absent placeholder rows have no l.id, so key the reminder toggle by member+date.
          const remKey = `${l.memberId}-${l.workDate}`;
          const remOpen = reminderDetails.isOpen(remKey);
          return (
            <React.Fragment key={`${l.id}-${idx}`}>
            <div className="hist-grid hist-row">
              <div className="hist-date">{moment(l.workDate).format("MMM D")}</div>
              <div className="hist-mem" role="button" tabIndex={0} title="View profile" onClick={() => goToProfile(l.memberId)} onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && goToProfile(l.memberId)}>
                {l.avatar ? <img className="av av-30" src={l.avatar} alt="" style={{ objectFit: "cover" }} /> : <div className="av av-30 av-MH">{(l.name || "?").slice(0, 2).toUpperCase()}</div>}
                <div style={{ minWidth: 0 }}><div className="log-name">{l.displayName || l.name}</div><div className="log-role">{l.title || ""}</div></div>
              </div>
              <div className="log-time">{fmtTime(l.checkIn) || "—"}</div>
              <div className="log-time">{l.checkout ? fmtTime(l.checkout) : "—"}{isOvernight(l) && (<span className="ovn-tip" title="Overnight shift — checked out the next day"><svg><use href="#i-info" /></svg></span>)}</div>
              <div className="log-time"><HoursCell log={l} hoursStr={hoursStr} /></div>
              <div className="log-break"><BreakCell log={l} breakStr={breakStr} expanded={brkOpen} onToggle={breakDetails.toggle} /></div>
              <div><ReminderCell count={l.reminderCount} rowKey={remKey} expanded={remOpen} onToggle={reminderDetails.toggle} /></div>
              <div>{l.checkIn ? <span className="pill-type" style={{ color: `var(--${remote ? "remote" : "office"})`, background: `var(--${remote ? "remote" : "office"}-bg)` }}><svg><use href={`#i-${remote ? "wifi" : "building"}`} /></svg>{remote ? "Remote" : "Office"}</span> : <span style={{ color: "var(--muted-2)" }}>—</span>}</div>
              <div><span className={`status-pill ${sCls}`}>{sLabel}</span></div>
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                {l.id ? <button className="row-edit" title="Edit entry" onClick={() => openEdit(l)}><svg><use href="#i-edit" /></svg></button> : null}
              </div>
            </div>
            {brkOpen && <BreakDetailRow breaks={breakDetails.breaksById[l.id]} loading={breakDetails.loadingId === l.id} />}
            {remOpen && <ReminderDetailRow breakdown={l.reminderBreakdown} />}
            </React.Fragment>
          );
        })}
        </div>

        <div className="hist-foot">
          <span>Showing {firstRow}–{lastRow} of {totalItems.toLocaleString()} records</span>
          <Pagination totalItems={totalItems} currentPage={page} pageSize={pageSize} onPageChange={setPage} />
        </div>
      </div>

      {editing && (
        <EditEntryModal
          log={editing}
          onClose={() => setEditing(null)}
          onSaved={applyUpdatedLog}
          onDelete={(log) => { setEditing(null); handleDelete(log); }}
        />
      )}
      <ConfirmModal {...modalProps} />
    </div>
  );
};

export default History;
